import type * as React from 'react'
import { memo } from 'react'

import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import styles from './ShowMoreLessButtons.module.css'

interface ShowMoreLessButtonsProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'onClick' | 'children'
> {
  itemsToRender?: unknown[]
  limit: number
  setLimit: (limit: number) => void
  defaultLimit?: number
  compact?: boolean
}

const DEFAULT_ITEMS_LIMIT = 3
function ShowMoreLessButtonsRaw({
  itemsToRender,
  limit,
  setLimit,
  defaultLimit = DEFAULT_ITEMS_LIMIT,
  compact = false,
  ...props
}: ShowMoreLessButtonsProps): React.ReactNode {
  const showMore = itemsToRender && itemsToRender.length > limit
  const showLess =
    itemsToRender && itemsToRender.length === limit && limit !== defaultLimit

  if (compact) {
    const remainingCount = itemsToRender ? itemsToRender.length - limit : 0
    if (showMore) {
      return (
        <UnstyledButton
          className={styles.compactButton}
          onClick={(): void => setLimit(itemsToRender.length)}
          aria-label={`Show ${remainingCount} more items`}
        >
          +{remainingCount}
        </UnstyledButton>
      )
    }
    if (showLess) {
      return (
        <UnstyledButton
          className={styles.compactButton}
          onClick={(): void => setLimit(defaultLimit)}
          aria-label="Show fewer items"
        >
          <ChevronUpIcon size={12} />
        </UnstyledButton>
      )
    }
    return null
  }

  if (showMore)
    return (
      <Button
        variant="tertiary"
        size="small"
        onClick={(): void => setLimit(itemsToRender.length)}
        {...props}
      >
        <ChevronDownIcon /> {itemsToRender.length - limit} More
      </Button>
    )

  if (showLess)
    return (
      <Button
        variant="tertiary"
        size="small"
        onClick={(): void => setLimit(defaultLimit)}
        {...props}
      >
        <ChevronUpIcon /> Show less
      </Button>
    )

  return null
}

export default memo(ShowMoreLessButtonsRaw)
